/**
 * @file Helper for Blob files
 */

/**
 * Triggers a download of a Blob object as a file with the specified filename.
 * This is an alternative to triggering a download by redirecting with window.location.href (which cannot be cancelled by the user)
 * Useful when the href call would have been a request with a long response time.
 * If cleanup is true, the blob URL is revoked after the print action is triggered. Cleanup is usually not desired if you want to redirect the user to the blob URL.
 * @param blob - The Blob object to download.
 * @param filename - The name of the file to download.
 * @param cleanup - Whether to clean up the created URL and link element after the download is complete. Defaults to true.
 * @returns The URL of the created object URL.
 */
const triggerDownloadFromBlob = (blob: Blob, filename: string, cleanup = true): string => {
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  link.click()

  // Cleanup
  if (cleanup) {
    URL.revokeObjectURL(url)
    link.remove()
  }

  return url
}

/**
 * Opens a new window with the given blob URL and triggers a print action when the window is loaded.
 * If cleanup is true, the blob URL is revoked after the print action is triggered. Cleanup is usually not desired if you want to redirect the user to the blob URL.
 * @param blob - The blob to print.
 * @param cleanup - Whether to revoke the blob URL after printing. Defaults to true.
 * @returns The blob URL.
 */
const triggerPrintFromBlob = (blob: Blob, cleanup = true): string => {
  const url = URL.createObjectURL(blob)

  const windowForUrl = window.open(url)
  windowForUrl?.addEventListener('load', () => {
    windowForUrl.print()
  })

  // Cleanup
  if (cleanup) {
    URL.revokeObjectURL(url)
  }

  return url
}

export { triggerDownloadFromBlob, triggerPrintFromBlob }
